import { z } from "zod";
import { RequirementSchema, VisibilitySchema } from "./common";

const ResolvedResponseOptionSchema = z.object({
  id: z.number(),
  feel_id: z.string(),
  text: z.string(),
  parent_label: z.string().nullable(),
});

const SelectResponseTypeSchema = z.object({
  _type: z.literal("select"),
  variants: z.array(ResolvedResponseOptionSchema),
});

const SelectOrOtherResponseTypeSchema = z.object({
  _type: z.literal("select-or-other"),
  variants: z.array(ResolvedResponseOptionSchema),
});

const MultiSelectResponseTypeSchema = z.object({
  _type: z.literal("multi-select"),
  variants: z.array(ResolvedResponseOptionSchema),
});

const MultiSelectGroupedResponseTypeSchema = z.object({
  _type: z.literal("multi-select-grouped"),
  groups: z.array(
    z.object({
      label: z.string(),
      variants: z.array(ResolvedResponseOptionSchema),
    })
  ),
});

const CheckboxResponseTypeSchema = z.object({
  _type: z.literal("checkbox"),
});

const NumberRangeResponseTypeSchema = z.object({
  _type: z.literal("number-range"),
  min: z.object({
    value: z.number(),
    color: z.enum(["positive", "negative", "neutral"]).optional(),
    label: z.string().optional(),
  }),
  max: z.object({
    value: z.number(),
    color: z.enum(["positive", "negative", "neutral"]).optional(),
    label: z.string().optional(),
  }),
});

const IntegerResponseTypeSchema = z.object({
  _type: z.literal("integer"),
  min: z.number(),
  max: z.number(),
});

const VasResponseTypeSchema = z.object({
  _type: z.literal("vas"),
  min: z.number(),
  max: z.number(),
});

const StarsResponseTypeSchema = z.object({
  _type: z.literal("stars"),
});

const DateResponseTypeSchema = z.object({
  _type: z.literal("date"),
});

const TextResponseTypeSchema = z.object({
  _type: z.literal("text"),
  min_length: z.number().optional(),
  max_length: z.number().optional(),
  multiline: z.boolean(),
});

const TextAreaResponseTypeSchema = z.object({
  _type: z.literal("text-area"),
});

const MultiSelectOrOtherResponseTypeSchema = z.object({
  _type: z.literal("multi-select-or-other"),
  variants: z.array(ResolvedResponseOptionSchema),
});

const ResolvedResponseTypeSchema = z.discriminatedUnion("_type", [
  SelectResponseTypeSchema,
  SelectOrOtherResponseTypeSchema,
  MultiSelectResponseTypeSchema,
  MultiSelectOrOtherResponseTypeSchema,
  MultiSelectGroupedResponseTypeSchema,
  CheckboxResponseTypeSchema,
  NumberRangeResponseTypeSchema,
  IntegerResponseTypeSchema,
  VasResponseTypeSchema,
  StarsResponseTypeSchema,
  DateResponseTypeSchema,
  TextResponseTypeSchema,
  TextAreaResponseTypeSchema,
]);

export type ResolvedResponseType = z.infer<typeof ResolvedResponseTypeSchema>;

export const ResolvedQuestionSchema = z.object({
  id: z.number(),
  feel_id: z.string(),
  _type: z.literal("question"),
  question: z.string(),
  description: z.string().optional(),
  semantic_key: z.string().optional().nullable(),
  option_semantic_keys: z.record(z.string(), z.string()).optional(),
  response_type: ResolvedResponseTypeSchema.nullable(),
});

export type ResolvedQuestion = z.infer<typeof ResolvedQuestionSchema>;

export const ResolvedQuestionRefSchema = z.object({
  question: ResolvedQuestionSchema,
  visibility: VisibilitySchema,
  requirement: RequirementSchema,
});

export type ResolvedQuestionRef = z.infer<typeof ResolvedQuestionRefSchema>;

export const QuestionGroupSchema = z.object({
  _type: z.literal("question_group"),
  id: z.number(),
  name: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  questions: z.array(ResolvedQuestionRefSchema),
});

export type QuestionGroup = z.infer<typeof QuestionGroupSchema>;

export const PartSchema = z.discriminatedUnion("_type", [
  z.object({
    _type: z.literal("question"),
    question: ResolvedQuestionSchema,
    visibility: VisibilitySchema,
    requirement: RequirementSchema.optional(),
  }),
  z.object({
    _type: z.literal("question_group"),
    visibility: VisibilitySchema,
    question_group: QuestionGroupSchema,
  }),
]);

export type Part = z.infer<typeof PartSchema>;

export const PageSchema = z.object({
  title: z.string().optional(),
  parts: z.array(PartSchema),
});

export type Page = z.infer<typeof PageSchema>;

export const QuestionnaireSchema = z.object({
  id: z.number(),
  name: z.string(),
  pages: z.array(PageSchema),
});

export type Questionnaire = z.infer<typeof QuestionnaireSchema>;
